import React, { useState, useEffect, ChangeEvent } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import { Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import cn from 'clsx';
import '../admin-study-data-sets.scss';
import Button from '@components/core/buttons';
import CharactersRemaining from '@components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogTitle } from '@components/core/dialog';
import ModalUpdatedInfo from '@components/core/modal-updated-info';
import TextField from '@components/core/text-field';
import { EditDataSetDialogProps } from '@pages/admin/study/admin-study-data-sets/edit-data-set-dialog/edit-data-set-dialog.interface';
import { useBoolean, useTranslation } from '@hooks/index';
import { formatDateUTC_DEPRECATED } from '@utils/convertor';
import { validateFields } from '@utils/validation';
import { SAVE_DATASET } from '@queries/central-monitoring';
import { CentralMonitoringObservationAndActionFields } from '@queries/generated/fragments.generated';

const maxLength = 4000;

export const EditDataSetDialog: React.FC<EditDataSetDialogProps> = ({ open, close, currentDataSet, observations }) => {
    const disableUpdateBtn = useBoolean(true);
    const { t } = useTranslation();
    const [saveDataSet, { loading }] = useMutation(SAVE_DATASET);

    const [notesValue, setNotesValue] = useState('');
    const [notesError, setNotesError] = useState<string | null>(null);
    const isActive = useBoolean(false);
    const isChangesApplied = useBoolean(false);

    const setInitialValues = () => {
        setNotesValue(currentDataSet.notes);
        isActive.setValue(currentDataSet.active);
    };

    useEffect(() => {
        const initialValues = {
            notes: currentDataSet?.notes,
            active: currentDataSet?.active,
        };
        const newValues = {
            notes: notesValue,
            active: isActive.value,
        };
        isChangesApplied.setValue(JSON.stringify(initialValues) !== JSON.stringify(newValues));
    }, [notesValue, isActive.value]);

    useEffect(() => {
        if (currentDataSet) setInitialValues();
    }, [currentDataSet]);

    const validateNotes = (value: string) => {
        const { notes } = validateFields(value, 'notes', maxLength);
        setNotesError(notes);
    };

    const handleActiveChange = (e: CheckboxChangeEvent) => {
        disableUpdateBtn.setFalse();
        isActive.toggle();
    };

    const handleNotesChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        disableUpdateBtn.setFalse();
        setNotesValue(e.target.value);
        setNotesError(null);
        validateNotes(e.target.value);
    };

    const onClose = () => {
        setInitialValues();
        setNotesError(null);
        close();
    };

    const handleSubmit = () => {
        saveDataSet({
            variables: {
                id: currentDataSet.id,
                active: isActive.value,
                notes: notesValue,
            },
        })
            .then(() => {
                close('updated');
            })
            .catch(() => {
                close('error');
            });
        disableUpdateBtn.setTrue();
    };

    if (!currentDataSet) return null;

    const isVisibleDisabled =
        Boolean(currentDataSet?.reports.length) ||
        observations.findIndex(
            (el: CentralMonitoringObservationAndActionFields) => el.dataSetId === currentDataSet.id,
        ) >= 0 ||
        (!currentDataSet.created && !isActive.value);

    return (
        <CustomDialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            scroll="paper"
            className="custom-width-dataset-popup"
        >
            <DialogTitle>
                {t('uam:editDataSetTitle', { id: currentDataSet.recordId })}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={onClose}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="edit-dataset-dialog-content">
                <div className="checkbox-wrapper">
                    <Checkbox
                        rounded={null}
                        className="no-border"
                        checked={isActive.value}
                        onChange={handleActiveChange}
                        disabled={isVisibleDisabled}
                        label={t('uam:visible')}
                        id="edit-dataset-visible"
                        labelClassName={cn({ 'checkbox-label-disabled': isVisibleDisabled })}
                        data-testid="edit-dataset-visible"
                        size="medium"
                    />
                </div>
                <div className="main-content-wrapper">
                    <p className="main-content-p">
                        <span className="bold-text">{t('uam:editDataSetDate')}: </span>
                        {formatDateUTC_DEPRECATED(currentDataSet.dataCutDate, true, 'DD-MMM-YYYY')}
                    </p>
                </div>

                <TextField
                    error={!!notesError}
                    label={t('uam:notes')}
                    id="edit-dataset-notes"
                    fullWidth
                    multiline
                    minRows={6}
                    value={notesValue}
                    onChange={handleNotesChange}
                    placeholder={t('uam:editDataSetNotesPlaceholder')}
                    helperText={
                        <CharactersRemaining
                            id="edit-dataset-help-text"
                            length={notesValue?.length ?? 0}
                            max={maxLength}
                            error={notesError}
                        />
                    }
                    variant="outlined"
                    color="primary"
                    className="before-select mb"
                />

                <DialogActions className="edit-dataset-dialog-content-actions">
                    <ModalUpdatedInfo item={currentDataSet} />
                    <Button
                        disabled={disableUpdateBtn.value || !!notesError || loading || !isChangesApplied.value}
                        className="edit-dataset-btn"
                        text={t('common:update')}
                        onClick={handleSubmit}
                        id="edit-dataset-btn"
                    />
                </DialogActions>
            </DialogContent>
        </CustomDialog>
    );
};
